export { C as CacheService } from './cache.service-0f1ca5b9.js';
export { b as MEDIA_QUERY_SIZES, a as MediaQueryChangeEvent, M as MediaQueryService, W as WB_RESIZE_EVENT_NAME } from './media-query.service-c1d687d9.js';
export { D as DirectionService, b as SCROLL_LOCK_CLASS, S as ScrollLockService, a as ScrollService } from './scroll.service-d46d571e.js';
export { a as applyAttribute } from './apply-attribute-91afd8fa.js';
export { a as applyClass } from './apply-class-e24c2f7f.js';
export { d as debounce } from './debounce-d29d0bc3.js';
export { V as Vector2d, g as getEventCoordinates } from './get-event-coordinates-ca0078ec.js';
export { g as getPrecision } from './get-precision-f05235b7.js';
export { g as getRandomId } from './get-random-id-39805d43.js';
export { g as getViewportWidth } from './get-viewport-width-1dbd5917.js';
export { h as hasRTLContext } from './has-rtl-context-8d0c1a2f.js';
export { i as isElementInViewport } from './is-element-in-viewport-3483c8f8.js';
export { i as isInternetExplorer } from './is-internet-explorer-44ea311e.js';
export { i as isSafari } from './is-safari-bbbcf093.js';
export { b as DEFAULT_FALLBACK_SCROLL_POLL_PX, c as DEFAULT_ROOT_ELEMENT, D as DEFAULT_ROOT_MARGIN, a as DEFAULT_THRESHOLD, d as ViewportObserver, V as ViewportObserverOptions, i as isValidNumber } from './viewport-observer-2e71fc9b.js';
export { m as monkeyPatch } from './monkey-patch-ec5353b8.js';
export { q as querySelectorAllAsArray } from './query-selector-all-as-array-88af38a2.js';
export { t as throttle } from './throttle-fe73760c.js';
export { a as WorkbenchRuntimeError, W as WorkbenchValidationError } from './errors-962d4543.js';
export { D as DEFAULT_WORKBENCH_THEME } from './themes-1a53774b.js';
export { I as Icon } from './icons-665eee89.js';

/**
 * @name coerceBoolean
 * @deprecated will be removed in 7.0.0
 * @description Translates a value to its boolean representation
 *
 * @example
 * import { coerceBoolean } from '@workbench/core';
 *
 * coerceBoolean(true); // true
 * coerceBoolean(false); // false
 * coerceBoolean('true'); // true
 * coerceBoolean('false'); // false
 * coerceBoolean('something'); // true
 * coerceBoolean(''); // true
 * coerceBoolean(1); // true
 * coerceBoolean(0); // false
 * coerceBoolean(undefined); // false
 * coerceBoolean(null); // false
 */
function coerceBoolean(value) {
  if (value === 0 || value === undefined || value === null) {
    return false;
  }
  else if (value === true || value === false) {
    return Boolean(value);
  }
  else if (value === 'false') {
    return false;
  }
  else {
    return true;
  }
}

/**
 * @name formatToCurrency
 *
 * @description Returns the given number formatted as a price for the given locale
 *
 * @example
 * import { formatToCurrency } from '@workbench/core';
 *
 * const formattedPrice: string = formatToCurrency(120000, 'es-ES', 'EUR', 0, 0);
 */
const formatToCurrency = (value, locale, currency, maximumFractionDigits = 0, minimumFractionDigits = 0) => {
  return Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(value);
};

/**
 * @name formatToMarketSpecificCurrency
 *
 * @description Provides special formatting for selected Mercedes-Benz markets, which don't represent the international default format. Use `formatToCurrency()` for international standard currency formatting.
 *
 * @example
 * import { formatToMarketSpecificCurrency } from '@workbench/core';
 *
 * formatToMarketSpecificCurrency(120000, 'it-CH'); // '1’234’568 CHF'
 * formatToMarketSpecificCurrency(120000, 'unsupported-LOCALE'); // '120000'
 */
const formatToMarketSpecificCurrency = (value, locale, config = SPECIAL_PRICE_FORMAT) => {
  if (!config[locale]) {
    return value.toString();
  }
  const priceFormatConfig = config[locale];
  const { currencyFormat = {}, decimalDigitsCount = 0, localeOverwrite: localeOverwrite = '' } = priceFormatConfig.priceFormat;
  const { priceCurrencyPrefix = '', priceCurrencySuffix = '' } = currencyFormat;
  const priceNumber = new Intl.NumberFormat(localeOverwrite || locale, { style: 'currency', currency: 'EUR', minimumFractionDigits: decimalDigitsCount, maximumFractionDigits: decimalDigitsCount })
    .format(value)
    .replace(/\s*€\s*/, '')
    .replace(/\s*EUR\s*/, '');
  return `${priceCurrencyPrefix}${priceNumber}${priceCurrencySuffix}`.trim();
};
const SPECIAL_PRICE_FORMAT = {
  'cs-CZ': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' Kč',
      },
    },
  },
  'da-DK': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' kr',
      },
    },
  },
  'de-AT': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '€ ',
      },
      decimalDigitsCount: 2,
    },
  },
  'de-CH': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' CHF',
      },
    },
  },
  'de-DE': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'de-HU': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' Ft',
      },
    },
  },
  'de-LU': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
    },
  },
  'el-CY': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '€',
      },
      decimalDigitsCount: 2,
    },
  },
  'el-GR': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-AE': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' USD',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-AU': {
    priceFormat: {
      decimalDigitsCount: 2,
    },
  },
  'en-CH': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' CHF',
      },
    },
  },
  'en-CN': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' RMB',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-DE': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-EG': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: 'EGP ',
      },
    },
  },
  'en-GB': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '£',
      },
    },
  },
  'en-HK': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: 'HK$',
      },
    },
  },
  'en-HU': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' Ft',
      },
    },
  },
  'en-IE': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
    },
  },
  'en-IN': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '₹ ',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-KR': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' KRW',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-MX': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '$',
      },
    },
  },
  'en-MY': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' MYR',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-NZ': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' NZD',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-SE': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' kr',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-SG': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' SGD',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-TH': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' Baht',
      },
      decimalDigitsCount: 2,
    },
  },
  'en-TW': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' 台幣',
      },
    },
  },
  'en-VN': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' ₫',
      },
    },
  },
  'en-ZA': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: 'R ',
      },
    },
  },
  'es-AR': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' ARS',
      },
    },
  },
  'es-ES': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'es-MX': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '$',
      },
    },
  },
  'fi-FI': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'fr-BE': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
    },
  },
  'fr-CH': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' CHF',
      },
    },
  },
  'fr-FR': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
    },
  },
  'fr-LU': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
    },
  },
  'hi-IN': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '₹ ',
      },
    },
  },
  'hu-HU': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' Ft',
      },
    },
  },
  'it-CH': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' CHF',
      },
    },
  },
  'it-IT': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'ja-JP': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '￥',
        priceCurrencySuffix: '～',
      },
      decimalDigitsCount: 0,
    },
  },
  'ko-KR': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' KRW',
      },
    },
  },
  'nl-BE': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
    },
  },
  'nl-NL': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '€ ',
      },
    },
  },
  'no-NO': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: 'kr ',
        priceCurrencySuffix: ',-',
      },
    },
  },
  'pl-PL': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' zł',
      },
    },
  },
  'pt-BR': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: 'R$ ',
      },
      decimalDigitsCount: 2,
    },
  },
  'pt-PT': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'ro-RO': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'ru-RU': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' руб.',
      },
      decimalDigitsCount: 2,
    },
  },
  'sk-SK': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'sl-SI': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' €',
      },
      decimalDigitsCount: 2,
    },
  },
  'sr-RS': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' EUR',
      },
      decimalDigitsCount: 2,
    },
  },
  'sv-SE': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' kr',
      },
    },
  },
  'th-TH': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' บาท',
      },
      decimalDigitsCount: 2,
    },
  },
  'tr-TR': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' TL',
      },
    },
  },
  'tr-ZY': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' TL',
      },
    },
  },
  'vi-VN': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' ₫',
      },
    },
  },
  'zh-CN': {
    priceFormat: {
      decimalDigitsCount: 2,
    },
  },
  'zh-HK': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: 'HK$',
      },
    },
  },
  'zh-TW': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: '$',
      },
    },
  },
  'hr-BA': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' KM',
      },
      decimalDigitsCount: 2,
    },
  },
  'uk-UA': {
    priceFormat: {
      currencyFormat: {
        priceCurrencySuffix: ' ₴',
      },
      decimalDigitsCount: 2,
    },
  },
  'ar-EG': {
    priceFormat: {
      currencyFormat: {
        priceCurrencyPrefix: 'EGP ',
      },
      decimalDigitsCount: 0,
      localeOverwrite: 'en-US',
    },
  },
};

const PIXELS_PER_REM = 16;
/**
 * @name getGutterWidth
 *
 * @description Returns the current gutter width in pixels, depending on the current viewport, as a number
 *
 * @example
 * import { getGutterWidth } from '@workbench/core';
 *
 * getGutterWidth(); // 16
 */
function getGutterWidth() {
  const gutterWithRemUnit = getComputedStyle(document.documentElement).getPropertyValue('--wb-grid-gutter-width');
  const gutterInPixels = Number(gutterWithRemUnit.replace('rem', '')) * PIXELS_PER_REM;
  return gutterInPixels;
}

/**
 * @name getViewportHeight
 *
 * @description Returns the current viewport height in pixels
 *
 * @example
 * import { getViewportHeight } from '@workbench/core';
 *
 * getViewportHeight(); // 768
 */
function getViewportHeight() {
  return Math.max(window.document.documentElement.clientHeight || 0, window.innerHeight || 0);
}

/**
 * @name hasOverflow
 *
 * @description Returns a true if the given element is overflowing on either the y- or x-axis
 *
 * @example
 * import { hasOverflow } from '@workbench/core';
 *
 * const hasOverflow: boolean = hasOverflow(someElement);
 */
const hasOverflow = (element) => {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
};

/**
 * @name haveSameItems
 *
 * @description Returns true if all values from both arrays are also present in the other array
 *
 * @example
 * import { haveSameItems } from '@workbench/core';
 *
 * const item1 = 'Lorem';
 * const item2 = 'Ipsum';
 * const item3 = 'Dolor';
 *
 * const myArray1 = [item1, item2];
 * const myArray2 = [item3, item2, item1];
 *
 * haveSameItems(myArray1, myArray2); // false
 *
 * myArray1.push(item3);
 *
 * haveSameItems(myArray1, myArray2); // true
 */
const haveSameItems = (arr1 = [], arr2 = []) => {
  return arr1.every((i) => arr2.includes(i)) && arr2.every((i) => arr1.includes(i));
};

/**
 * @name isValueInEnum
 *
 * @description Returns true if a value is part of a given enum
 *
 * @example
 * import { isValueInEnum } from '@workbench/core';
 *
 * isValueInEnum('February', MONTHS); // true
 */
const isValueInEnum = (valuetoCheck, enumToCheck) => {
  try {
    return Object.values(enumToCheck).includes(valuetoCheck);
  }
  catch (_) {
    return false;
  }
};

export { coerceBoolean, formatToCurrency, formatToMarketSpecificCurrency, getGutterWidth, getViewportHeight, hasOverflow, haveSameItems, isValueInEnum };
